// ** React Imports
import { Fragment, lazy } from "react"
import { Navigate } from "react-router-dom"
// ** Layouts
import BlankLayout from "@layouts/BlankLayout"
import VerticalLayout from "@src/layouts/VerticalLayout"
import HorizontalLayout from "@src/layouts/HorizontalLayout"
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper"

// ** Route Components
import PublicRoute from "@components/routes/PublicRoute"

// ** Utils
import { isObjEmpty, isUserLoggedIn } from "@utils"

const getLayout = {
    blank: <BlankLayout />,
    vertical: <VerticalLayout />,
    horizontal: <HorizontalLayout />
}

// ** Document title
// const TemplateTitle = "%s - Vuexy React Admin Template"
const TemplateTitle = 'Seafarer'
// ** Default Route
let DefaultRoute = "/home"

const Dashboard = lazy(() => import("../../views/Dashboard/InvoiceAmountDashboard"))
const CustomerDashboard = lazy(() => import("../../views/Dashboard/CustomerDashboard"))
const EmployeeDashboard = lazy(() => import("../../views/Dashboard/EmployeeDashboard"))
const AgentDashboard = lazy(() => import("../../views/Dashboard/AgentDashboard"))
const ManagementDashboard = lazy(() => import("../../views/Dashboard/SeaManagement/SeaManagementDashboard"))
const CrewInfo = lazy(() => import("../../views/Dashboard/SeaManagement/crewInfo"))
const SEAPortalRevenueDashboard = lazy(() => import("../../views/Dashboard/SEAPortalRevenueDashboard"))
const AppVersionsDashboard = lazy(() => import("../../views/Dashboard/AppVersionsDashboard"))
const KPIDashboard = lazy(() => import("../../views/Dashboard/KPIDashboard"))
const Login = lazy(() => import("../../views/Login"))
const Register = lazy(() => import("../../views/Register"))
const ForgotPassword = lazy(() => import("../../views/ForgotPassword"))
const Error = lazy(() => import("../../views/Pages/Error"))
const Unauthorized = lazy(() => import("../../views/Pages/Unauthorized"))
const Workorder = lazy(() => import("../../views/Workorder"))
const MyProfile = lazy(() => import("../../views/MyProfile"))
const PageRefresh = lazy(() => import("../../views/PageRefresh"))
const Home = lazy(() => import("../../views/Dashboard/Home"))
const UserManagement = lazy(() => import("../../views/Dashboard/UserManagement"))
const UserAgentManagement = lazy(() => import("../../views/Dashboard/Management/index"))
const FilingAgentDashboard = lazy(() => import("../../views/FA/Dashboard/index"))
const SearchDashboard = lazy(() => import("../../views/Dashboard/Inquiry/Index"))
const MSearchDashboard = lazy(() => import("../../views/Dashboard/SearchDashboard"))
const DoctorDetailsDashboard = lazy(() => import("../../views/Dashboard/DoctorDetailsDashboard"))
const DocumentPopupPage = lazy(() => import("../../views/FA/SBA/DocumentPopupPage"))
const BookRedirection = lazy(() => import("../../views/Dashboard/Inquiry/BookRedirection"))
const SQCRedirection = lazy(() => import("../../views/Dashboard/Inquiry/SQCRedirection"))
const LicenseRedirection = lazy(() => import("../../views/Dashboard/Inquiry/LicenseREdirection"))
let Routes = []

const getRouteMeta = (route) => {
    if (isObjEmpty(route.element.props)) {
        if (route.meta) {
            return { routeMeta: route.meta }
        } else {
            return {}
        }
    }
}

// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout, defaultLayout) => {
    const LayoutRoutes = []
    if (isUserLoggedIn()) {

        const baseRoutes = [
            { path: "/", index: true, element: <Navigate replace to={DefaultRoute} /> },
            { path: "/:catCode/:typeCode/save", element: <Workorder /> },
            { path: "/supportingDoc/:woId", element: <DocumentPopupPage /> },
            { path: "/:catCode/:typeCode/:woId", element: <Workorder /> },
            { path: "/FA/dashboard", element: <FilingAgentDashboard /> },
            { path: "/home", element: <Home /> },
            { path: "/my-profile", element: <MyProfile /> },
            { path: "/pageRefresh", element: <PageRefresh /> },
            { path: "/Unauthorized", element: <Unauthorized /> },
            { path: "*", element: <Error /> }
        ]

        if (JSON.parse(isUserLoggedIn())?.isEmployee) {
            let sessionRoute = sessionStorage.getItem('UrlPath')
            let rolesRoute = false
            let userManagementRoute = false
            let superAdminRoute = false

            const userData = JSON.parse(localStorage.getItem('userData'))
            const userRoles = userData && userData.userRoles
            if (userRoles) {

                const findRole = userRoles.find(find => (find.rId === 1 || find.rId === 6 || find.rId === 7))
                if (findRole) {
                    rolesRoute = true
                }
                const userManagementRole = userRoles.find(find => (find.rId === 1 || find.rId === 4 || find.rId === 5))
                if (userManagementRole) {
                    userManagementRoute = true
                }
                const adminRole = userRoles.find(find => (find.rId === 1))
                if (adminRole) {
                    superAdminRoute = true
                }
            }

            if (sessionRoute === '/Unauthorized') {
                const userData = JSON.parse(localStorage.getItem('userData'))
                const userRoles = userData && userData.userRoles
                if (userRoles && userRoles.length > 0) {
                    sessionRoute = '/home'
                }
            }

            DefaultRoute = sessionRoute ? sessionRoute : '/home'

            // **Employee Merge Routes
            Routes = [
                ...baseRoutes,
                { path: "/workorders", element: <EmployeeDashboard /> },
                { path: "/medical-clinics/inquiry", element: <MSearchDashboard /> },
                { path: "/medical-clinics/doctor-details", element: <DoctorDetailsDashboard /> },
                { path: "/sea-management", element: <ManagementDashboard /> },
                { path: "/sea-management/crew-info/:crewId", element: <CrewInfo /> },
                ...(superAdminRoute ? [
                    { path: "/filing-agent", element: <Dashboard /> },
                    { path: "/filing-agent/agent", element: <AgentDashboard /> },
                    { path: "/admin/finance", element: <SEAPortalRevenueDashboard /> },
                    { path: "/admin/appSettings", element: <AppVersionsDashboard /> },
                    { path: "/filing-agent/kpi", element: <KPIDashboard /> },
                    { path: "/filing-agent/inquiry", element: <SearchDashboard /> },
                    { path: "/filing-agent/inquiry/Book/:bookId", element: <BookRedirection /> },
                    { path: "/filing-agent/inquiry/SQC/:SQCId", element: <SQCRedirection /> },
                    { path: "/filing-agent/inquiry/License/:LICId", element: <LicenseRedirection /> }
                ] : []),
                ...(userManagementRoute ? [{ path: "/filing-agent/management", element: <UserAgentManagement /> }] : []),
                ...(rolesRoute ? [{ path: "/role-management", element: <UserManagement /> }] : [])
            ]
        } else {
            // **Customer Merge Routes
            // if (JSON.parse(isUserLoggedIn())?.isEmployee) {
            let sessionRoute = sessionStorage.getItem('UrlPath')
            if (sessionRoute === '/Unauthorized') {
                sessionRoute = '/home'
            }
            DefaultRoute = sessionRoute ? sessionRoute : '/home'
            const userData = JSON.parse(localStorage.getItem('userData'))
            const data = userData && userData?.companies
            // Condition 1: All numbers
            const hasAllNumbers = data?.some(item => /^\d+$/.test(item.agentId))
            // // Condition 2: Starts with MTTCA
            // const hasMTTCA = data.some(item => item.agentId?.startsWith("MTTCA"))
            // // Condition 3: Starts with SFR
            // const hasSFR = data.some(item => item.agentId?.startsWith("SFR"))
            // // Condition 4: Starts with MC
            // const hasMC = data.some(item => item.agentId?.startsWith("MC"))

            Routes = [
                ...baseRoutes,
                { path: "/workorders", element: <CustomerDashboard /> },
                ...(hasAllNumbers ? [
                    { path: "/filing-agent/agent", element: <AgentDashboard /> },
                    { path: "/sea-management", element: <ManagementDashboard /> },
                    { path: "/sea-management/crew-info/:crewId", element: <CrewInfo /> },
                    { path: "/filing-agent/kpi", element: <KPIDashboard /> }
                ] : []),
                { path: "/management", element: <UserAgentManagement /> }
            ]
        }
    }
    if (Routes) {
        Routes.filter((route) => {
            let isBlank = false
            // ** Checks if Route layout or Default layout matches current layout
            if (
                (route.meta && route.meta.layout && route.meta.layout === layout) ||
                ((route.meta === undefined || route.meta.layout === undefined) &&
                    defaultLayout === layout)
            ) {
                const RouteTag = PublicRoute

                // ** Check for public or private route
                if (route.meta) {
                    route.meta.layout === "blank" ? (isBlank = true) : (isBlank = false)
                }
                if (route.element) {
                    const Wrapper =
                        // eslint-disable-next-line multiline-ternary
                        isObjEmpty(route.element.props) && isBlank === false
                            ? // eslint-disable-next-line multiline-ternary
                            LayoutWrapper
                            : Fragment

                    route.element = (
                        <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
                            <RouteTag route={route}>{route.element}</RouteTag>
                        </Wrapper>
                    )
                }

                // Push route to LayoutRoutes
                LayoutRoutes.push(route)
            }
            return LayoutRoutes
        })
    }
    return LayoutRoutes
}

const getRoutes = (layout) => {
    const defaultLayout = layout || "vertical"
    const layouts = ["vertical", "horizontal", "blank"]

    const AllRoutes = []

    layouts.forEach((layoutItem) => {
        const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout)

        AllRoutes.push({
            path: "/",
            element: getLayout[layoutItem] || getLayout[defaultLayout],
            children: LayoutRoutes
        })
    })
    return AllRoutes
}

export { DefaultRoute, TemplateTitle, Routes, getRoutes }
